/**
 * `<ChangeDigestPanel/>` — "what changed since you last looked" (issue #765,
 * B3.6): a gentle, member-scoped orientation aid shown on opening the map.
 *
 * BRAND-CRITICAL (§A9 — the digest is THE surveillance-risk vector). This
 * panel renders ONLY the member's OWN slice, exactly as the endpoint returns
 * it (the server already scopes to: new tasks on the member's branch, state
 * changes on the member's nodes, notes that mention the member). It is a
 * calm orientation aid, NEVER a feed of what other people did, NEVER a count
 * or tally about anyone. The panel:
 *   - shows nothing about other people's activity;
 *   - adds no aggregation of its own;
 *   - is dismissable, on the member's terms ("Just your slice.").
 *
 * Faithful to `B3-6-what-changed.svg`:
 *   - header "Since your last visit (<date>)" + a dismiss affordance;
 *   - "On your work" — state changes ("X was marked done") + new sub-tasks
 *     ("Y added under Z (yours)");
 *   - "Mentions" — notes that mention you, each with an "open →";
 *   - footer "Just your slice." + an "open my work →" action.
 *
 * Editorial revamp (#1043, Wave 2): theme-aware via `useMapTheme()`. The flag
 * stays OFF for every team (default LEGACY) until the whole revamp lands, so:
 *   - LEGACY (flag off): the shipped soft-mint card — teal section headers,
 *     "Since your last visit (<date>)" header, the indigo "open my work →".
 *     Byte-identical to today.
 *   - EDITORIAL (flag on): the wireframe `V_B36` floating `.t-card` — a cream
 *     surface ringed in `c.ink` with a soft shadow; a mono `SINCE YOUR LAST
 *     VISIT · <date>` kicker + a serif-italic "Catch up on your slice."
 *     heading + a `dismiss ✕`; an `ON YOUR WORK` section (done-tint rows for
 *     "marked done", a teal `NEW +` issue tag for added sub-tasks); a
 *     `MENTIONS` section; a `c.bgRecess` footer "Just your slice." + the ink
 *     "open my work →". RE-SKIN, not rebuild: every `data-testid`, copy, and
 *     behaviour are preserved (only the look forks).
 *
 * Dismiss: clicking dismiss hides the panel for this visit AND advances the
 * member's stored "last looked" stamp (so next visit's `since` starts here) —
 * the caller owns the stamp write via `onDismiss`, keeping the panel pure UI.
 *
 * Graceful degradation: a not-yet-deployed endpoint, or an empty slice, simply
 * renders nothing (the calm default — no panel, no noise). Never a crash.
 *
 * Isolation: taskmap-local imports only — no scheduler coupling.
 */

import type { ChangeDigest, DigestMention } from "../api/useTaskChangeDigest";
import { formatEventStamp } from "../history/formatTime";
import { useMapTheme } from "../theme/MapThemeContext";
import { taskStateStyle } from "../tree/state";

// Legacy palette (byte-identical to the shipped soft-mint card).
const TEAL = "#1F7A6E";
const INK = "#0C2A2A";
const MUTED = "#9CA3AF";
const SOFT_BORDER = "#CFE0D8";
const INDIGO = "#4F46E5";
const HAIRLINE = "#F3F4F6";

export interface ChangeDigestPanelProps {
  /** The member's OWN slice (already server-scoped). */
  digest: ChangeDigest;
  /** Dismiss the panel + advance "last looked" (caller-owned). */
  onDismiss: () => void;
  /** "open my work →" — jump to the member's own-work lens (optional). */
  onOpenMyWork?: (() => void) | undefined;
  /** Open a mentioned node (the per-mention "open →"; optional). */
  onOpenNode?: ((nodeId: string) => void) | undefined;
  /**
   * The team's IANA timezone (e.g. `"Asia/Singapore"`) — the "since" header
   * renders the last-looked stamp as date + HH:MM in this zone (#906/#932).
   * When omitted the viewer's local zone is used.
   */
  teamTimezone?: string | undefined;
}

export function ChangeDigestPanel(props: ChangeDigestPanelProps) {
  const { digest, onDismiss, onOpenMyWork, onOpenNode, teamTimezone } = props;

  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  const stamp = formatEventStamp(digest.since, teamTimezone);

  // Resolved tones — legacy keeps its exact shipped hexes; editorial reads the
  // theme palette + the four-state styles via `theme.stateStyle`.
  const sectionInk = editorial ? c.muted : TEAL;
  const bodyInk = editorial ? c.ink : INK;
  const mutedInk = editorial ? c.muted : MUTED;
  const cardBorder = editorial ? c.ink : SOFT_BORDER;
  const hairline = editorial ? c.rule : HAIRLINE;
  const recessBg = editorial ? c.bgRecess : "transparent";
  const doneStyle = editorial ? theme.stateStyle("done") : taskStateStyle("done");
  const labelFont = editorial ? theme.fonts.mono : undefined;

  const newTasks = Array.isArray(digest.new_tasks) ? digest.new_tasks : [];
  const stateChanges = Array.isArray(digest.state_changes) ? digest.state_changes : [];
  const mentions: DigestMention[] = Array.isArray(digest.mentions) ? digest.mentions : [];

  const hasWork = newTasks.length > 0 || stateChanges.length > 0;
  const hasMentions = mentions.length > 0;

  return (
    <div
      data-testid="change-digest-panel"
      className="mx-4 mt-4 rounded-xl border bg-white"
      style={{
        borderColor: cardBorder,
        borderWidth: editorial ? 1 : undefined,
        backgroundColor: editorial ? c.cardBg : undefined,
        // Editorial: the floating `.t-card` soft drop-shadow (`V_B36`).
        boxShadow: editorial ? "0 12px 36px rgba(0,0,0,0.10)" : "0 1px 2px 0 rgb(0 0 0 / 0.05)",
      }}
    >
      {/* Header. Editorial (`V_B36`): a mono `SINCE YOUR LAST VISIT · <date>`
          kicker + a serif-italic "Catch up on your slice." heading + a
          `dismiss ✕`. Legacy: the bold "Since your last visit (<date>)" line +
          a `dismiss` button. The since-stamp renders in the team tz (#906/#932). */}
      <div className="flex items-start justify-between gap-3 px-5 py-3.5">
        {editorial ? (
          <div className="min-w-0">
            <div
              data-testid="digest-since-kicker"
              className="text-[10px] uppercase"
              style={{ fontFamily: labelFont, letterSpacing: "0.08em", color: c.muted }}
            >
              Since your last visit · {stamp}
            </div>
            <div
              data-testid="digest-heading"
              className="mt-1 text-[18px] italic"
              style={{ fontFamily: theme.fonts.serif, color: c.ink }}
            >
              Catch up on your slice.
            </div>
          </div>
        ) : (
          <div className="text-sm font-bold" style={{ color: bodyInk }}>
            Since your last visit ({stamp})
          </div>
        )}
        <button
          type="button"
          data-testid="digest-dismiss"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="shrink-0 rounded-md border px-2.5 py-1 text-[11px] transition-colors hover:opacity-90"
          style={{
            borderColor: editorial ? "transparent" : SOFT_BORDER,
            color: editorial ? c.muted : "#5C7878",
          }}
        >
          {editorial ? "dismiss ✕" : "dismiss"}
        </button>
      </div>
      <div className="h-px" style={{ backgroundColor: hairline }} />

      {/* On your work — state changes + new sub-tasks (the member's OWN nodes). */}
      {hasWork && (
        <div className="px-5 py-3" data-testid="digest-on-your-work">
          <div
            className={editorial ? "text-[10px] uppercase" : "text-[12px] font-bold"}
            style={{
              color: sectionInk,
              fontFamily: labelFont,
              letterSpacing: editorial ? "0.08em" : undefined,
            }}
          >
            On your work
          </div>
          <ul className="mt-2 flex flex-col gap-2.5">
            {stateChanges.map((change) => {
              const toStyle = editorial
                ? theme.stateStyle(change.state_to)
                : taskStateStyle(change.state_to);
              return (
                <li
                  key={`s-${change.id}`}
                  data-testid="digest-state-change"
                  className={
                    editorial
                      ? "flex items-start gap-2.5 rounded-md px-2.5 py-2"
                      : "flex items-start gap-2.5"
                  }
                  style={editorial ? { backgroundColor: doneStyle.chipBg } : undefined}
                >
                  {editorial ? (
                    <span
                      aria-hidden
                      className="mt-0.5 shrink-0 text-[10px] uppercase"
                      style={{
                        fontFamily: labelFont,
                        letterSpacing: "0.08em",
                        color: doneStyle.dot,
                      }}
                    >
                      STATE +
                    </span>
                  ) : (
                    <span
                      aria-hidden
                      className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px]"
                      style={{ backgroundColor: toStyle.chipBg, color: toStyle.fg }}
                    >
                      {toStyle.glyph}
                    </span>
                  )}
                  <div className="min-w-0">
                    {!editorial && (
                      <div className="text-[10px]" style={{ color: mutedInk }}>
                        state change
                      </div>
                    )}
                    <div className="text-[12px]" style={{ color: bodyInk }}>
                      “{change.title}” is now {toStyle.label}
                    </div>
                  </div>
                </li>
              );
            })}
            {newTasks.map((task) => (
              <li
                key={`n-${task.id}`}
                data-testid="digest-new-task"
                className={
                  editorial
                    ? "flex items-start gap-2.5 rounded-md px-2.5 py-2"
                    : "flex items-start gap-2.5"
                }
                style={editorial ? { backgroundColor: c.bgRecess } : undefined}
              >
                {editorial ? (
                  <span
                    aria-hidden
                    className="mt-0.5 shrink-0 text-[10px] uppercase"
                    style={{ fontFamily: labelFont, letterSpacing: "0.08em", color: c.teal }}
                  >
                    NEW +
                  </span>
                ) : (
                  <span aria-hidden className="mt-0.5 shrink-0 text-[14px]" style={{ color: TEAL }}>
                    +
                  </span>
                )}
                <div className="min-w-0">
                  {!editorial && (
                    <div className="text-[10px]" style={{ color: mutedInk }}>
                      new sub-task
                    </div>
                  )}
                  <div className="text-[12px]" style={{ color: bodyInk }}>
                    “{task.title}” added on your branch
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasWork && hasMentions && (
        <div className="mx-5 h-px" style={{ backgroundColor: hairline }} />
      )}

      {/* Mentions — notes that mention YOU (your own slice). */}
      {hasMentions && (
        <div className="px-5 py-3" data-testid="digest-mentions">
          <div
            className={editorial ? "text-[10px] uppercase" : "text-[12px] font-bold"}
            style={{
              color: sectionInk,
              fontFamily: labelFont,
              letterSpacing: editorial ? "0.08em" : undefined,
            }}
          >
            Mentions
          </div>
          <ul className="mt-2 flex flex-col gap-2">
            {mentions.map((mention, i) => {
              const nodeId = mention.node_id;
              const nodeTitle = mention.node_title || "a task";
              const noteId = mention.note_id ?? String(i);
              return (
                <li
                  key={noteId}
                  data-testid="digest-mention"
                  className={
                    editorial
                      ? "flex items-center justify-between gap-2 rounded-md px-2.5 py-2"
                      : "flex items-center justify-between gap-2"
                  }
                  style={editorial ? { backgroundColor: c.bgRecess } : undefined}
                >
                  <span
                    className="flex min-w-0 items-center gap-2 text-[11px]"
                    style={{ color: editorial ? c.slate : MUTED }}
                  >
                    <span aria-hidden>💬</span>
                    <span className="truncate">A note on “{nodeTitle}” mentioned you</span>
                  </span>
                  {nodeId && onOpenNode && (
                    <button
                      type="button"
                      data-testid="digest-mention-open"
                      onClick={() => onOpenNode(nodeId)}
                      className="shrink-0 rounded-md border px-2.5 py-1 text-[11px] transition-colors hover:opacity-90"
                      style={{
                        borderColor: editorial ? "transparent" : SOFT_BORDER,
                        color: editorial ? c.teal : "#5C7878",
                      }}
                    >
                      open →
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="mx-5 h-px" style={{ backgroundColor: hairline }} />

      {/* Footer: "Just your slice." + open-my-work. Editorial: a `c.bgRecess`
          footer band + the ink "open my work →"; legacy keeps the indigo. */}
      <div
        className={
          editorial
            ? "flex items-center justify-between gap-3 px-5 py-3"
            : "flex items-center justify-between gap-3 px-5 py-3"
        }
        style={
          editorial ? { backgroundColor: recessBg, borderTop: `1px solid ${c.rule}` } : undefined
        }
      >
        <span
          className="text-[11px] italic"
          style={{
            color: mutedInk,
            fontFamily: editorial ? labelFont : undefined,
            letterSpacing: editorial ? "0.04em" : undefined,
          }}
        >
          Just your slice.
        </span>
        {onOpenMyWork && (
          <button
            type="button"
            data-testid="digest-open-my-work"
            onClick={onOpenMyWork}
            className="rounded-md border px-3 py-1 text-[11px] font-semibold transition-colors hover:opacity-90"
            style={
              editorial
                ? { backgroundColor: c.ink, borderColor: c.ink, color: c.cardBg }
                : { backgroundColor: "#EEF2FF", borderColor: INDIGO, color: INDIGO }
            }
          >
            open my work →
          </button>
        )}
      </div>
    </div>
  );
}
